import { DndContext, DragMoveEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { useContext, useMemo, useRef, useState } from 'react'
import { useRafLoop } from 'react-use'
import { Range } from 'types'
import { MediaTrimmerWithoutContext } from '../../../media-trimmer'
import { UgeArtboardContext } from '../../contexts/artboard'
import { Playhead } from './style'

type Props = {
    className?: string
}

const UgeArtboardTrimmer = ({ className }: Props) => {
    const pausedStateRef = useRef<boolean>(false)
    const { duration, paused, getTime, goToTime, source, timing, setPaused, setTiming } = useContext(UgeArtboardContext)
    const [playheadTime, setPlayheadTime] = useState<number>(0)
    const pointerSensor = useSensor(PointerSensor)
    const sensors = useSensors(pointerSensor)

    const trimmedDuration = useMemo(() => {
        if (timing) {
            return timing.end - timing.start
        }

        return duration
    }, [duration, timing])

    const onDragMove = (e: DragMoveEvent | DragStartEvent) => {
        if (!e.active.data.current) return

        const { controlType, controlValue } = e.active.data.current

        switch (controlType) {
            case 'range-end':
                const end = (controlValue as Range).end * duration
                setTiming(timing ? { ...timing, end } : { start: 0, end })
                goToTime(end)
                break
            case 'range-start':
                const start = (controlValue as Range).start * duration
                setTiming(timing ? { ...timing, start } : { start, end: duration })
                goToTime(start)
                break
            case 'playhead':
                goToTime((timing?.start || 0) + (controlValue as number) * trimmedDuration)
                break
        }
    }

    const onDragStart = (e: DragStartEvent) => {
        pausedStateRef.current = paused
        setPaused(true)
        onDragMove(e)
    }

    const onDragEnd = () => {
        setPaused(pausedStateRef.current)
    }

    useRafLoop(() => {
        const time = getTime()

        setPlayheadTime(time - (timing?.start || 0))
    })

    return (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd}>
            <MediaTrimmerWithoutContext
                className={className}
                disabled={!duration}
                dndMode="handles-only"
                duration={duration}
                source={source}
                size="large"
                timing={timing}
                onChange={setTiming}
            >
                <Playhead duration={trimmedDuration} time={playheadTime} size="large" scrubbable />
            </MediaTrimmerWithoutContext>
        </DndContext>
    )
}

export default UgeArtboardTrimmer
